import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';
import circleCross from 'public/icons/circle-cross.svg';
import circlePlus from 'public/icons/circle-plus.svg';
import { mergeDisplayError } from 'helper/anytime-booking';
import { recommendedVehicleTypeSelector } from 'selectors/new-booking-selectors';
import { codAdditionalServiceID } from 'constants/anytime-booking-constants';
import { Col, Row, Tooltip } from 'grab-ui';
import cx from 'classnames';
import I18n from 'i18n-js';
import _get from 'lodash/get';
import styles from './styles.less';

const AdditionalServices = ({ dispatch, delivery }) => {
  const validAdditionalServices = _get(
    delivery,
    'config.additionalServices',
    [],
  ).filter(ast => ast.id !== codAdditionalServiceID);

  const currentSelectedServices = _get(
    delivery,
    'detail.configuration.additionalServices',
    [],
  ).filter(ast => ast.id !== codAdditionalServiceID);

  const selectedAST = useMemo(
    () => new Map(currentSelectedServices.map(ast => [ast.id, true])),
    [currentSelectedServices],
  );

  const onAdditionalServiceClick = useCallback(
    (additionalService, isRemove) => () => {
      if (isRemove) {
        dispatch(
          NewBookingDuck.creators.removeAdditionalService(
            delivery.id,
            additionalService.id,
          ),
        );

        dispatch(
          ScribeDuck.creators.scribeExpressDeliveryDetailDeleteAddOn({
            deliveryID: delivery.id,
          }),
        );
      } else {
        dispatch(
          NewBookingDuck.creators.addAdditionalService(delivery.id, {
            id: additionalService.id,
            data: {
              enabled: true,
            },
          }),
        );

        dispatch(
          ScribeDuck.creators.scribeExpressDeliveryDetailAddOn({
            deliveryID: delivery.id,
          }),
        );
      }
    },
    [dispatch],
  );

  const showAdditionalServices = useMemo(
    () => {
      const detail = _get(delivery, 'detail');
      const pickUpPOI = _get(detail, 'pickUp.poi');
      const dropOffPOI = _get(detail, 'dropOffs[0].poi');
      const serviceType = _get(detail, 'configuration.serviceType');
      const vehicleType =
        _get(detail, 'configuration.vehicleType') ||
        recommendedVehicleTypeSelector(delivery);
      if (!(pickUpPOI && dropOffPOI && serviceType && vehicleType)) {
        return false;
      }
      return (
        validAdditionalServices.filter(
          additionalService => !_get(additionalService, 'errorMessage'),
        ).length > 0
      );
    },
    [delivery, validAdditionalServices],
  );

  if (!showAdditionalServices) {
    return null;
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {I18n.t('v2.anytimeBooking.deliveryForm.additionalServices')}
      </div>
      <Row type="flex" gutter={16} className={styles.additionalServices}>
        {validAdditionalServices.map(additionalService => {
          const errorMessage = _get(additionalService, 'errorMessage');
          const { hint } = mergeDisplayError(errorMessage);
          const selected = selectedAST.get(additionalService.id);
          const disabled = !!errorMessage;

          const component = (
            <a
              key={additionalService.id}
              className={cx(styles.additionalServiceButton, {
                [styles.disabled]: disabled,
                [styles.selected]: selected,
              })}
              onClick={
                disabled
                  ? undefined
                  : onAdditionalServiceClick(additionalService, selected)
              }
            >
              <span> {additionalService.name} </span>
              <img
                className={styles.icon}
                src={selected ? circleCross : circlePlus}
                alt=""
              />
            </a>
          );
          return (
            <Col
              xs={24}
              md={8}
              className={styles.additionalServiceCol}
              key={additionalService.id}
            >
              {disabled ? (
                <Tooltip
                  key={additionalService.id}
                  title={
                    hint ||
                    I18n.t('v2.anytimeBooking.deliveryForm.onlyOneAddOn')
                  }
                  placement={'bottom'}
                >
                  {component}
                </Tooltip>
              ) : (
                component
              )}
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

AdditionalServices.propTypes = {
  dispatch: PropTypes.func.isRequired,
  delivery: NewBookingDeliveryPropType.isRequired,
};

export default connect()(AdditionalServices);
